import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FolderInput, Download, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { createPortfolio } from "@/services/portfolio/PortfolioService";
import { insertAllocationsBulk } from "@/services/portfolio/PortfolioAllocationsService";
import { AiImportDialog, IdleBotSvg } from "@/components/assets/AiImportDialog";
import { logger } from "@/services/LoggingService";

interface ImportPortfolioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportPortfolioDialog({ open, onOpenChange }: ImportPortfolioDialogProps) {
  const navigate = useNavigate();
  const allocFileRef = useRef<HTMLInputElement | null>(null);
  const [allocFile, setAllocFile] = useState<File | null>(null);
  const assetsFileRef = useRef<HTMLInputElement | null>(null);
  const [assetFiles, setAssetFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragOverModel, setIsDragOverModel] = useState(false);
   const [activeTab, setActiveTab] = useState<'model' | 'assets' | 'templates'>('model');
   const [showAiDialog, setShowAiDialog] = useState(false);
   const [aiInitialFiles, setAiInitialFiles] = useState<File[]>([]);

  const scheduleDialogCloseCleanup = useCallback(() => {
    const cleanup = () => {
      try {
        const wrapper = document.querySelector('.group\\/sidebar-wrapper') as HTMLElement | null;
        if (wrapper?.hasAttribute('inert')) wrapper.removeAttribute('inert');
        if (wrapper?.getAttribute('aria-hidden') === 'true') wrapper.removeAttribute('aria-hidden');

        const hasOpenRadix = !!document.querySelector('[data-radix-portal] [data-state="open"]');

        let didUnlock = false;
        let bodyComputedPointerEvents: string | null = null;
        let rootComputedPointerEvents: string | null = null;
        let htmlComputedPointerEvents: string | null = null;

        if (!hasOpenRadix) {
          const body = document.body;
          bodyComputedPointerEvents = body ? getComputedStyle(body).pointerEvents : null;
          const bodyInlinePointerEvents = body?.style?.pointerEvents || '';
          const bodyHasInlineNone = bodyInlinePointerEvents === 'none' || (body?.getAttribute('style') || '').includes('pointer-events: none');
          if (bodyHasInlineNone || bodyComputedPointerEvents === 'none') {
            body.style.pointerEvents = 'auto';
            didUnlock = true;
          }
          if (body?.style?.overflow === 'hidden') {
            body.style.overflow = '';
          }

          const root = document.getElementById('root');
          rootComputedPointerEvents = root ? getComputedStyle(root).pointerEvents : null;
          const rootInlinePointerEvents = root?.style?.pointerEvents || '';
          const rootHasInlineNone = rootInlinePointerEvents === 'none' || (root?.getAttribute('style') || '').includes('pointer-events: none');
          if (root && (rootHasInlineNone || rootComputedPointerEvents === 'none')) {
            root.style.pointerEvents = 'auto';
            didUnlock = true;
          }

          const html = document.documentElement;
          htmlComputedPointerEvents = html ? getComputedStyle(html).pointerEvents : null;
          const htmlInlinePointerEvents = html?.style?.pointerEvents || '';
          const htmlHasInlineNone = htmlInlinePointerEvents === 'none' || (html?.getAttribute('style') || '').includes('pointer-events: none');
          if (htmlHasInlineNone || htmlComputedPointerEvents === 'none') {
            html.style.pointerEvents = 'auto';
            didUnlock = true;
          }
          if (html?.style?.overflow === 'hidden') {
            html.style.overflow = '';
          }
        }

        if (didUnlock) {
          const inertCount = document.querySelectorAll('[inert]').length;
          logger.debug('ImportPortfolioDialog', 'Dialog closed cleanup completed', {
            inertCount,
            hasOpenRadix,
            bodyPointerEvents: document.body?.style?.pointerEvents || null,
            bodyComputedPointerEvents,
            bodyOverflow: document.body?.style?.overflow || null,
            rootPointerEvents: document.getElementById('root')?.style?.pointerEvents || null,
            rootComputedPointerEvents,
            htmlPointerEvents: document.documentElement?.style?.pointerEvents || null,
            htmlComputedPointerEvents,
          });
        }
      } catch (error) {
        logger.warn('ImportPortfolioDialog', 'Dialog closed cleanup failed', { error: String(error) });
      }
    };

    // Important: do not clear these timers on unmount. Portfolio model import navigates
    // immediately after closing, and we still need this cleanup to run on the next page.
    [0, 50, 150, 400, 800, 1200, 2000].forEach((ms) => window.setTimeout(cleanup, ms));
  }, []);

  useEffect(() => {
    if (open) return;
    scheduleDialogCloseCleanup();
  }, [open, scheduleDialogCloseCleanup]);
  const onSelectAllocFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setAllocFile(selected[0] || null);
  };
  const onSelectAssetFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setAssetFiles(selected);
  };

  const [submittingAlloc, setSubmittingAlloc] = useState(false);
  const [submittingAssets, setSubmittingAssets] = useState(false);

  const allowedClasses = new Set(["equity","fixed_income","alternative","cash","multi_asset"]);

  // Model name input state
  const [defaultModelName] = useState<string>(() => `Imported Model (${new Date().toLocaleString()})`);
  const [modelName, setModelName] = useState<string>(defaultModelName);
  const [modelNameDirty, setModelNameDirty] = useState<boolean>(false);

  const sanitizeModelName = useCallback((s: string): string => {
    const cleaned = String(s || '').replace(/[^A-Za-z0-9 ()_:\-.,]/g, '').trim();
    return cleaned.slice(0, 120);
  }, []);

  type AllocationCsvRow = {
    asset_type?: string;
    allocation_percentage?: string | number | null;
    allocation_value?: string | number | null;
    asset_name?: string | null;
    currency?: string | null;
  };

  async function parseAllocationsCsv(file: File): Promise<AllocationCsvRow[]> {
    // Robust CSV parser for our template with BOM/header alias handling and blank-row skipping
    const raw = await file.text();
    const text = raw.replace(/^\uFEFF/, ""); // strip BOM if present
    const rawLines = text.split(/\r?\n/);
    if (rawLines.length === 0) return [];

    const aliasMap: Record<string, keyof AllocationCsvRow> = {
      asset_type: 'asset_type',
      'asset class': 'asset_type',
      'asset type': 'asset_type',
      asset_class: 'asset_type',
      class: 'asset_type',
      type: 'asset_type',
      allocation_percentage: 'allocation_percentage',
      'allocation percent': 'allocation_percentage',
      'allocation %': 'allocation_percentage',
      percentage: 'allocation_percentage',
      percent: 'allocation_percentage',
      pct: 'allocation_percentage',
      weight: 'allocation_percentage',
      'target %': 'allocation_percentage',
      'target_percent': 'allocation_percentage',
      'target percent': 'allocation_percentage',
      allocation_value: 'allocation_value',
      value: 'allocation_value',
      amount: 'allocation_value',
      dollar: 'allocation_value',
      '$': 'allocation_value',
      'allocation ($)': 'allocation_value',
      asset_name: 'asset_name',
      name: 'asset_name',
      label: 'asset_name',
      currency: 'currency',
      ccy: 'currency',
      cur: 'currency',
      'currency code': 'currency',
    };

    const headerLine = (rawLines[0] || '').replace(/^\uFEFF/, '');
    const headerRaw = headerLine.split(',').map(h => h.replace(/^\uFEFF/, '').trim().replace(/^"|"$/g, '').toLowerCase());
    const header = headerRaw.map(h => aliasMap[h] || (h as keyof AllocationCsvRow));

    const rows: AllocationCsvRow[] = [];
    for (let i = 1; i < rawLines.length; i++) {
      const line = rawLines[i] ?? '';
      if (!line) continue;
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      const isAllEmpty = cols.every(c => c === '' || c === undefined);
      if (isAllEmpty) continue;
      const obj: any = {};
      header.forEach((h, idx) => {
        const key = h as keyof AllocationCsvRow;
        obj[key] = cols[idx] ?? '';
      });
      rows.push(obj as AllocationCsvRow);
    }
    return rows;
  }

  function toNumber(val: any): number | null {
    const n = Number(String(val ?? "").replace(/[,%\s]/g, ""));
    return Number.isFinite(n) ? n : null;
  }

  async function getUserDefaults(): Promise<{ userId: string | null; defaultCurrency: string }>{
    try {
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess?.session?.user?.id || null;
      if (uid) {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('default_currency')
          .eq('id', uid)
          .maybeSingle();
        const dc = ((prof as any)?.default_currency || 'CAD');
        return { userId: uid, defaultCurrency: String(dc || 'CAD').toUpperCase() };
      }
    } catch {}
    return { userId: null, defaultCurrency: 'CAD' };
  }

  async function handleImportAllocations() {
    if (!allocFile || submittingAlloc) return;
    setSubmittingAlloc(true);
    let toastId: string | number | undefined;
    try {
      console.log('ImportPortfolioDialog: Start Import (model) clicked', { hasFile: !!allocFile });
      toastId = toast.loading('Importing model…', { description: 'Parsing CSV' });
      const rows = await parseAllocationsCsv(allocFile);
      if (!rows.length) throw new Error('No rows found in CSV');

      const cleaned = rows.map((r) => ({
        asset_type: String((r as any).asset_type || '').replace(/\uFEFF/g, '').trim().toLowerCase().replace(/[\s-]/g, '_'),
        allocation_percentage: toNumber(r.allocation_percentage),
        allocation_value: toNumber(r.allocation_value),
        asset_name: (r.asset_name ?? null) as any,
        currency: (r.currency ? String(r.currency).toUpperCase() : null),
      }));

      const classMap: Record<string, string> = {
        equities: 'equity',
        equity: 'equity',
        stocks: 'equity',
        stock: 'equity',
        fixed_income: 'fixed_income',
        bonds: 'fixed_income',
        bond: 'fixed_income',
        fixedincome: 'fixed_income',
        cash: 'cash',
        cash_equivalents: 'cash',
        cash_equivalent: 'cash',
        cash_and_equivalents: 'cash',
        alternative: 'alternative',
        alternatives: 'alternative',
        alts: 'alternative',
        crypto: 'alternative',
        real_estate: 'alternative',
        multi_asset: 'multi_asset',
        multiasset: 'multi_asset',
        balanced: 'multi_asset',
      };

      const normalized = cleaned.map(r => ({
        ...r,
        asset_type: classMap[r.asset_type || ''] || r.asset_type,
      }));

      const validationErrors: string[] = [];
      normalized.forEach((r, idx) => {
        const isTypeMissing = !r.asset_type;
        const hasAnyValue = (r.allocation_percentage ?? null) !== null || (r.allocation_value ?? null) !== null;
        if (isTypeMissing && !hasAnyValue) {
          return;
        }
        if (!r.asset_type || !allowedClasses.has(r.asset_type)) {
          validationErrors.push(`Row ${idx + 2}: invalid asset_type "${r.asset_type}"`);
        }
      });
      if (validationErrors.length > 0) {
        const preview = validationErrors.slice(0, 3).join(' • ');
        const more = validationErrors.length > 3 ? ` • …and ${validationErrors.length - 3} more` : '';
        throw new Error(`${preview}${more}. Allowed: equity, fixed_income, alternative, cash, multi_asset.`);
      }

      const pctList = normalized.map((r) => r.allocation_percentage || 0).filter((v) => Number.isFinite(v as number)) as number[];
      const valList = normalized.map((r) => r.allocation_value || 0).filter((v) => Number.isFinite(v as number)) as number[];

      let finalPercents: number[] = [];
      if (pctList.length === cleaned.length) {
        const sum = pctList.reduce((s, v) => s + v, 0);
        const within = Math.abs(sum - 100) <= 0.1;
        if (!within) throw new Error(`Allocation percentages must sum to 100% (±0.1). Current sum: ${sum.toFixed(2)}%`);
        finalPercents = pctList.map((p) => Math.round(p * 100) / 100);
      } else if (valList.length === cleaned.length) {
        const total = valList.reduce((s, v) => s + v, 0);
        if (!(total > 0)) throw new Error('Allocation values must sum to a positive total');
        finalPercents = valList.map((v) => Math.round(((v / total) * 100) * 100) / 100);
        const sum2 = finalPercents.reduce((s, v) => s + v, 0);
        // Adjust rounding residual to 100 if close
        if (Math.abs(sum2 - 100) <= 0.1 && finalPercents.length > 0) {
          const residual = Math.round((100 - sum2) * 100) / 100;
          finalPercents[0] = Math.round((finalPercents[0] + residual) * 100) / 100;
        }
      } else {
        throw new Error('Provide either allocation_percentage for all rows, or allocation_value for all rows');
      }

      const { userId, defaultCurrency } = await getUserDefaults();
      if (!userId) throw new Error('User session not found');

      // Preserve exact legacy behavior if input was not changed by the user
      const effectiveModelName = modelNameDirty
        ? (sanitizeModelName(modelName) || `Imported Model (${new Date().toLocaleString()})`)
        : `Imported Model (${new Date().toLocaleString()})`;
      const portfolioId = await createPortfolio({ userId, name: effectiveModelName, portfolioType: 'model', portfolioSize: 100000, status: 'in_progress' });

      const payloads = normalized.map((r, idx) => ({
        portfolio_id: portfolioId,
        asset_id: null,
        asset_type: r.asset_type,
        allocation_percentage: finalPercents[idx],
        allocation_value: Math.round((100000 * (finalPercents[idx] / 100)) * 100) / 100,
        is_value_locked: false as any,
        currency_code: (r.currency || defaultCurrency),
      })) as any[];

      await insertAllocationsBulk(payloads as any);

      toast.success('Model imported', { description: `Created model portfolio with ${payloads.length} class allocations.`, id: toastId });
      setAllocFile(null);
      onOpenChange(false);
      scheduleDialogCloseCleanup();
      navigate(`/portfolios/build/${portfolioId}`);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('ImportPortfolioDialog: Model import failed', err);
      toast.error('Import failed', { description: err?.message || 'Unable to import allocations', id: toastId });
    } finally {
      setSubmittingAlloc(false);
    }
  }

  const handleImportAssets = useCallback(() => {
    if (!assetFiles.length || submittingAssets) return;
    setSubmittingAssets(true);
    setAiInitialFiles(assetFiles);
    setShowAiDialog(true);
    setAssetFiles([]);
    setSubmittingAssets(false);
    onOpenChange(false);
    scheduleDialogCloseCleanup();
  }, [assetFiles, submittingAssets, onOpenChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="p-0 sm:max-w-[920px] overflow-hidden">
          <div className="grid md:grid-cols-[1.35fr_1fr] h-full">
            {/* Left */}
            <div className="p-6 md:p-7 min-h-[460px]">
              <DialogHeader className="p-0">
                <DialogTitle className="flex items-center gap-2 text-2xl font-semibold">
                  <FolderInput className="h-5 w-5" />
                  Import Portfolio
                </DialogTitle>
                <DialogDescription>
                  Choose an import method. For target mixes, use the model (%) import. For holdings, use the AI-powered assets import.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-5 border-b mb-5">
                <div className="flex gap-6">
                  <button
                    className={`pb-2 relative ${activeTab === 'model' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('model')}
                  >
                    <span className="text-base font-medium">Model Portfolios</span>
                    {activeTab === 'model' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary" />}
                  </button>
                  <button
                    className={`pb-2 relative ${activeTab === 'assets' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('assets')}
                  >
                    <span className="text-base font-medium">Ai-Import Assets</span>
                    {activeTab === 'assets' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary" />}
                  </button>
                  <button
                    className={`pb-2 relative ${activeTab === 'templates' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('templates')}
                  >
                    <span className="text-base font-medium">Templates</span>
                    {activeTab === 'templates' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary" />}
                  </button>
                </div>
              </div>

              {activeTab === 'model' && (
                <div className="grid gap-6">
                  <div className="space-y-3">
                    <Label className="font-medium">Model Name</Label>
                    <Input
                      value={modelName}
                      onChange={(e) => { setModelName(e.target.value); setModelNameDirty(true); }}
                      placeholder={defaultModelName}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-3">
                    <Label className="font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload model allocation CSV
                    </Label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragOverModel(true); }}
                      onDragLeave={() => setIsDragOverModel(false)}
                      onDrop={(e) => { e.preventDefault(); setIsDragOverModel(false); const files = Array.from(e.dataTransfer.files || []); if (files.length) { setAllocFile(files[0]); } }}
                      className={`rounded-md border-dashed border p-3 transition-colors ${isDragOverModel ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-muted-foreground/40'}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => allocFileRef.current?.click()}
                      aria-label="Upload model CSV"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm">
                          <div className="font-medium">Upload File</div>
                          <div className="text-xs text-muted-foreground">CSV only • columns: asset_type, allocation_percentage or allocation_value, currency</div>
                        </div>
                        <Button size="sm" className="gap-2">
                          <Upload className="h-4 w-4" />
                          Choose File
                        </Button>
                      </div>
                      <input
                        ref={allocFileRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={onSelectAllocFile}
                      />
                    </div>
                    {allocFile && (
                      <div className="text-sm text-muted-foreground">{allocFile.name}</div>
                    )}
                  </div>

                  <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto min-h-[44px]" disabled={submittingAlloc}>Cancel</Button>
                    <Button type="button" onClick={() => { console.log('ImportPortfolioDialog: Click model import button'); handleImportAllocations(); }} disabled={!allocFile || submittingAlloc} className="w-full sm:w-auto min-h-[44px]">{submittingAlloc ? 'Importing…' : 'Start Import'}</Button>
                  </DialogFooter>
                </div>
              )}

              {activeTab === 'assets' && (
                <div className="grid gap-6">
                  <div className="space-y-3">
                    <Label className="font-medium flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Upload asset file(s)
                    </Label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const files = Array.from(e.dataTransfer.files || []); if (files.length) setAssetFiles(files as File[]); }}
                      className={`rounded-md border-dashed border p-3 transition-colors ${isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/20 hover:border-muted-foreground/40'}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => assetsFileRef.current?.click()}
                      aria-label="Upload files"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm">
                          <div className="font-medium">Upload Files</div>
                          <div className="text-xs text-muted-foreground">or drag & drop here</div>
                        </div>
                        <Button size="sm" className="gap-2">
                          <Upload className="h-4 w-4" />
                          Choose File
                        </Button>
                      </div>
                      <input
                        ref={assetsFileRef}
                        type="file"
                        accept="application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*"
                        className="hidden"
                        multiple
                        onChange={onSelectAssetFiles}
                      />
                    </div>
                    {assetFiles.length > 0 && (
                      <div className="text-sm text-muted-foreground">{assetFiles.length} file{assetFiles.length === 1 ? '' : 's'} selected</div>
                    )}
                  </div>
                  <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto min-h-[44px]" disabled={submittingAssets}>Cancel</Button>
                    <Button type="button" onClick={() => { console.log('ImportPortfolioDialog: Start Import (assets) clicked', { files: assetFiles.length }); handleImportAssets(); }} disabled={assetFiles.length === 0 || submittingAssets} className="w-full sm:w-auto min-h-[44px]">{submittingAssets ? 'Starting…' : 'Start Import'}</Button>
                  </DialogFooter>
                </div>
              )}

              {activeTab === 'templates' && (
                <div className="grid gap-6">
                  <div className="space-y-3">
                    <Label className="font-medium flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Download templates
                    </Label>
                    <ul className="list-disc pl-5 space-y-2 text-sm">
                      <li>
                        <a className="text-blue-600 hover:underline" href="/portfolio-allocation-template.csv" download="model-portfolio-template.csv">Model CSV</a>
                      </li>
                      <li>
                        <a className="text-blue-600 hover:underline" href="/portfolio-assets-template.csv" download="assets-import-template.csv">Asset CSV</a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center justify-center bg-muted/50 border-l">
              <div className="relative">
                <div className="absolute inset-0 -translate-x-6 -translate-y-6 blur-2xl rounded-full bg-primary/10" />
                <div className="relative rounded-xl border bg-background p-6 text-muted-foreground shadow-sm flex items-center justify-center">
                  <IdleBotSvg className="h-16 w-16" />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AiImportDialog open={showAiDialog} onOpenChange={setShowAiDialog} initialFiles={aiInitialFiles} />
    </>
  );
}
